import { Stack, Typography, useTheme } from "@mui/material";
import {
  AppIcon,
  BellIcon,
  CheckCircleIcon,
  MailIcon,
  MobileIcon,
  TeamsAppIcon,
} from "components";
import { NotifyDrawer } from "../NotifyDrawer";
import { useState } from "react";
import { Control, Controller } from "react-hook-form";
import {
  useUpdateMspResourceNotificationSettings,
  useUpdateMspUserNotificationSettings,
} from "controller/hooks/control-center/notifications";
import { RESOURCE_NOTIFY_METHODS, USER_NOTIFY_METHODS } from "./types";
import { useUpdateCustomerNotificationsSettings } from "controller";
import { ALL_ROLE_ID } from "shared";

export const NotificationSettingsChip = ({
  name,
  control,
  notifyMethod,
  roleTypes,
  isRoleTypesDataLoading,
  searchQuery,
  setSearchQuery,
  isChatBased,
  notificationType,
  isDisabled = false,
}: {
  name: string;
  control: Control<any>;
  notifyMethod: RESOURCE_NOTIFY_METHODS | USER_NOTIFY_METHODS;
  roleTypes: any[];
  isRoleTypesDataLoading: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  isChatBased: boolean;
  notificationType: "resource" | "user";
  isDisabled?: boolean;
}) => {
  const theme = useTheme();

  const ICONS = {
    web_app: (
      <AppIcon style={{ width: "18px", height: "18px" }} color="inherit" />
    ),
    email: (
      <MailIcon style={{ width: "18px", height: "18px" }} color="inherit" />
    ),
    resource_mobile_app: (
      <MobileIcon style={{ width: "18px", height: "18px" }} color="inherit" />
    ),
    agent_app_desktop: (
      <AppIcon style={{ width: "18px", height: "18px" }} color="inherit" />
    ),
    agent_mobile_app: (
      <MobileIcon style={{ width: "18px", height: "18px" }} color="inherit" />
    ),
    teams_app: (
      <TeamsAppIcon style={{ width: "18px", height: "18px" }} color="inherit" />
    ),
    notification_sound: (
      <BellIcon style={{ width: "18px", height: "18px" }} color="inherit" />
    ),
  };

  const LABELS = {
    web_app: "In-app",
    email: "Email",
    resource_mobile_app: "Push Notification",
    agent_app_desktop: "In-app",
    agent_mobile_app: "Push Notification",
    teams_app: "Teams App",
    notification_sound: "Notification Sound",
  };

  const [isNotifyDrawerOpen, setIsNotifyDrawerOpen] = useState(false);

  const { isLoading: isUpdateMspResourceNotificationSettingsLoading } =
    useUpdateMspResourceNotificationSettings();
  const { isLoading: isUpdateMspUserNotificationSettingsLoading } =
    useUpdateMspUserNotificationSettings();
  const { isLoading: isUpdateCustomerNotificationsSettingsLoading } =
    useUpdateCustomerNotificationsSettings();
  const isLoading =
    isUpdateMspResourceNotificationSettingsLoading ||
    isUpdateMspUserNotificationSettingsLoading ||
    isUpdateCustomerNotificationsSettingsLoading;
  const onChipClick = () => {
    !isLoading && !isDisabled && setIsNotifyDrawerOpen(true);
  };

  const getNotificationCount = (value: Array<any>) => {
    if (notificationType === "user") {
      return value.length === 2 ? "All" : value.length;
    } else {
      return value[0].role_id === ALL_ROLE_ID ? "All" : value.length;
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        return (
          <Stack direction="row" alignItems={"center"} gap="8px">
            <Stack
              direction="row"
              alignItems={"center"}
              gap="8px"
              borderRadius="32px"
              bgcolor={
                value?.length > 0
                  ? isDisabled
                    ? theme.palette.neutral["005"]
                    : theme.palette.secondary["purple-tint-3"]
                  : "#FFF"
              }
              border={
                value?.length > 0
                  ? isDisabled
                    ? `1px solid ${theme.palette.neutral["020"]}`
                    : `1px solid ${theme.palette.primary.main}`
                  : `1px solid ${theme.palette.neutral["010"]}`
              }
              sx={{
                height: "32px",
                padding: "8px 12px",
                cursor: isDisabled ? "default" : "pointer",
                transition: "all 0.3s",
                ...(isDisabled
                  ? {}
                  : {
                      ":hover": {
                        bgcolor: theme.palette.neutral["005"],
                        color: theme.palette.lightBg.main,
                        border: `1px solid ${theme.palette.neutral["015"]}`,
                      },
                    }),

                color: isDisabled
                  ? theme.palette.lightBg.low
                  : theme.palette.lightBg.high,
              }}
              onClick={onChipClick}
            >
              {ICONS[notifyMethod]}
              <Typography
                variant="body-small"
                sx={{
                  color: "inherit",
                }}
              >
                {LABELS[notifyMethod]}
              </Typography>
              {value?.length > 0 && (
                <Stack
                  direction="row"
                  sx={{
                    minWidth: "16px",
                    height: "16px",
                    borderRadius: "16px",
                    bgcolor: isDisabled
                      ? theme.palette.neutral["010"]
                      : theme.palette.primary.main,
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "2px 6px",
                  }}
                >
                  <Typography
                    variant="caption-xs"
                    sx={{
                      color: isDisabled ? theme.palette.lightBg.low : "#FFF",
                    }}
                  >
                    {getNotificationCount(value)}
                  </Typography>
                </Stack>
              )}
            </Stack>
            {notifyMethod !== "notification_sound" && (
              <NotifyDrawer
                open={isNotifyDrawerOpen}
                onClose={() => {
                  setIsNotifyDrawerOpen(false);
                  setSearchQuery("");
                }}
                notifyMethodIcon={ICONS[notifyMethod]}
                notifyMethodLabel={LABELS[notifyMethod]}
                onChange={onChange}
                value={value}
                roleTypes={roleTypes}
                isRoleTypesDataLoading={isRoleTypesDataLoading}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isChatBased={isChatBased}
                formFieldName={name}
              />
            )}
          </Stack>
        );
      }}
    ></Controller>
  );
};
